import { F0Box, F0Checkbox, F0Dialog, F0Text } from '@factorialco/f0-react'
import { useCallback, useState } from 'react'

import i18n from 'modules/core/lib/i18n'

import Launch from './Launch'
import ScheduleForLater from './ScheduleForLater'

type Mode = 'schedule' | 'launch'

interface ExistingSubmissionPeriod {
  id: string
  startDate: string
  endDate: string
}

interface Props {
  periodId: string
  companyId: string
  submissionPeriod?: ExistingSubmissionPeriod
  onClose: () => void
}

interface FormStatus {
  submit: () => void
  isSubmitting: boolean
  hasErrors: boolean
}

interface ModeOptionCardProps {
  mode: Mode
  selectedMode: Mode
  label: string
  onSelect: (mode: Mode) => void
}

const ModeOptionCard = ({ mode, selectedMode, label, onSelect }: ModeOptionCardProps) => {
  const isSelected = selectedMode === mode

  return (
    <button
      type="button"
      onClick={() => onSelect(mode)}
      style={{
        padding: 0,
        width: '100%',
        border: 'none',
        cursor: 'pointer',
        background: 'transparent',
      }}
    >
      <F0Box
        width="full"
        padding="md"
        display="flex"
        border="default"
        borderRadius="md"
        alignItems="center"
        justifyContent="between"
        background={isSelected ? 'selected' : 'primary'}
        borderColor={isSelected ? 'selected' : 'default'}
      >
        <F0Text variant="label" content={label} />
        {isSelected && (
          <F0Checkbox
            hideLabel
            title={label}
            checked={isSelected}
            onCheckedChange={() => onSelect(mode)}
          />
        )}
      </F0Box>
    </button>
  )
}

const SubmissionPeriodFormModal = ({ periodId, companyId, submissionPeriod, onClose }: Props) => {
  const isEditing = !!submissionPeriod
  const canLaunchNow = !submissionPeriod || new Date() < new Date(submissionPeriod.startDate)
  const [mode, setMode] = useState<Mode>('schedule')

  const [formStatus, setFormStatus] = useState<FormStatus>({
    submit: () => {},
    isSubmitting: false,
    hasErrors: false,
  })

  const handleStatusChange = useCallback((status: FormStatus) => {
    setFormStatus(status)
  }, [])

  const title = isEditing
    ? i18n.t('planning.submission_period.form.title_edit')
    : i18n.t('planning.submission_period.form.title_create')

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={title}
      primaryAction={{
        label: i18n.t('planning.submission_period.form.save'),
        onClick: formStatus.submit,
        loading: formStatus.isSubmitting,
        disabled: formStatus.hasErrors,
      }}
      secondaryAction={{
        label: i18n.t('planning.submission_period.form.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="sm" marginBottom="md">
        <F0Text
          variant="label"
          content={i18n.t('planning.submission_period.form.schedule_title')}
        />
        <F0Text
          variant="description"
          content={i18n.t('planning.submission_period.form.schedule_description')}
        />
      </F0Box>

      {canLaunchNow && (
        <F0Box display="flex" gap="sm" marginBottom="md">
          <ModeOptionCard
            mode="schedule"
            onSelect={setMode}
            selectedMode={mode}
            label={i18n.t('planning.submission_period.form.schedule_for_later')}
          />

          <ModeOptionCard
            mode="launch"
            onSelect={setMode}
            selectedMode={mode}
            label={i18n.t('planning.submission_period.form.launch_now')}
          />
        </F0Box>
      )}

      {mode === 'schedule' ? (
        <ScheduleForLater
          periodId={periodId}
          companyId={companyId}
          submissionPeriod={submissionPeriod}
          onClose={onClose}
          onStatusChange={handleStatusChange}
        />
      ) : (
        <Launch
          periodId={periodId}
          companyId={companyId}
          submissionPeriodId={submissionPeriod?.id}
          onClose={onClose}
          onStatusChange={handleStatusChange}
        />
      )}
    </F0Dialog>
  )
}

export default SubmissionPeriodFormModal
